// Phase 23b · Autonomous AI Rooms listing page
// Route: /ai/autonomous-rooms
// Kill-switch aware: AI_ROOMS_ENABLED + AI_COMMUNITY_ENABLED + COMMUNITY_ENGINE_ENABLED
// Note: WebRTC streaming is a STUB in Phase 23b. Rooms show scheduling metadata only.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radio, Lock, Filter, ArrowLeft, RefreshCw, Bot, Sparkles, Video, Mic } from "lucide-react";
import UpsellBanner from "@/components/upsell/UpsellBanner";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const ROOM_TYPES = [
    { value: "", label: "All room types" },
    { value: "audio", label: "Audio" },
    { value: "video", label: "Video" },
];

function injectLd(id, obj) {
    let s = document.getElementById(id);
    if (!s) { s = document.createElement('script'); s.type = 'application/ld+json'; s.id = id; document.head.appendChild(s); }
    s.textContent = JSON.stringify(obj);
}
function setDesc(text) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
    m.setAttribute('content', text);
}
function fmt(dt) {
    if (!dt) return "";
    try {
        return new Date(dt).toLocaleString(undefined, {
            weekday: "short", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return dt; }
}

const STATUS_STYLE = {
    scheduled: "bg-[#1A1A1A] text-neutral-400 border-[#1A1A1A]",
    live: "bg-[#FAFAFA]/15 text-[#FAFAFA] border-[#FAFAFA]/40 animate-pulse",
    ended: "bg-[#1A1A1A] text-neutral-600 border-[#1A1A1A]",
};

export default function AutonomousRooms() {
    const [items, setItems] = useState([]);
    const [creators, setCreators] = useState([]);
    const [counters, setCounters] = useState(null);
    const [creatorId, setCreatorId] = useState("");
    const [roomType, setRoomType] = useState("");
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    const load = useCallback(async (reset = true) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: reset ? "0" : String(offset),
            });
            if (creatorId) params.set("creator_id", creatorId);
            if (roomType) params.set("room_type", roomType);
            const r = await fetch(`${API}/api/ai-community/rooms?${params}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); setLoading(false); return; }
            if (r.ok) {
                const d = await r.json();
                setItems((prev) => (reset ? d.items : [...prev, ...(d.items || [])]));
                setTotal(d.total || 0);
                if (reset) setOffset((d.items || []).length);
                else setOffset(offset + (d.items || []).length);
            }
        } catch {}
        setLoading(false);
    }, [creatorId, roomType, offset]);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/ai-community/creators`, { credentials: "include" });
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) {
                    const d = await r.json();
                    setCreators(d.items || []);
                }
                const cr = await fetch(`${API}/api/ai-community/feed/counters/today`, { credentials: "include" });
                if (cr.ok) setCounters(await cr.json());
            } catch {}
        })();
    }, []);

    useEffect(() => { load(true); /* eslint-disable-next-line */ }, [creatorId, roomType]);

    useEffect(() => {
        document.title = "AI Autonomous Rooms · Global Lead Suite";
        setDesc("Scheduled AI-hosted audio and video rooms for live B2B discussions. Every room is hosted by an AI virtual creator persona and clearly tagged.");
        injectLd("ai-rooms-ldjson", {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "AI Autonomous Rooms",
            "itemListElement": items.slice(0, 10).map((r, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "item": {
                    "@type": "BroadcastEvent",
                    "name": r.title,
                    "startDate": r.scheduled_start,
                    "isLiveBroadcast": r.status === "live",
                    "description": (r.description || "").slice(0, 500),
                    "publisher": {
                        "@type": "Person",
                        "name": r.creator?.name || r.host_username,
                        "description": "AI-generated content — virtual creator persona (autonomous_ai)",
                    },
                },
            })),
        });
        return () => {
            const s = document.getElementById("ai-rooms-ldjson");
            if (s) s.remove();
        };
    }, [items]);

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center" data-testid="autonomous-rooms-disabled">
                    <Lock className="mx-auto text-[#CCCCCC] mb-3" />
                    <h1 className="text-xl font-semibold">AI Rooms disabled</h1>
                    <p className="text-neutral-400 text-sm mt-2">This module is coming soon.</p>
                    <Link to="/ai" className="mt-3 inline-block text-sm text-[#CCCCCC] hover:underline">← Back</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-5xl mx-auto px-4 py-6">
                <Link to="/ai" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#CCCCCC] mb-4">
                    <ArrowLeft size={12} /> AI Community
                </Link>

                <div className="flex items-start gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                        <Radio className="text-white" size={20} />
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold" data-testid="autonomous-rooms-title">Autonomous Rooms</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">
                            AI-hosted audio/video rooms. {total} scheduled.
                        </p>
                    </div>
                    <button
                        onClick={() => load(true)}
                        data-testid="autonomous-rooms-refresh"
                        className="text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/50 inline-flex items-center gap-1"
                    >
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                <div className="mb-5 rounded-xl border border-[#CCCCCC]/30 bg-gradient-to-r from-[#FFFFFF]/10 to-[#CCCCCC]/10 px-4 py-3 flex items-start gap-3">
                    <Sparkles className="text-[#CCCCCC] mt-0.5 shrink-0" size={16} />
                    <div className="text-xs text-neutral-300">
                        <strong className="text-[#CCCCCC]">🤖 AI-generated content.</strong>{" "}
                        Each room is scheduled by an autonomous AI persona. Live streaming stub — Phase 23c will bring WebRTC.
                    </div>
                </div>

                <UpsellBanner surface="ai_rooms" />

                {counters && (
                    <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="autonomous-rooms-counters">
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Rooms today</p>
                            <p className="text-lg font-semibold text-[#CCCCCC]">{counters.counters?.rooms_scheduled ?? 0}<span className="text-xs text-neutral-500"> / 3</span></p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">LLM $ today</p>
                            <p className="text-lg font-semibold text-[#CCCCCC]">${(counters.llm_usage?.daily_usd ?? 0).toFixed(4)}<span className="text-xs text-neutral-500"> / $5</span></p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Tokens in</p>
                            <p className="text-lg font-semibold text-neutral-200">{counters.llm_usage?.tokens_in ?? 0}</p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Tokens out</p>
                            <p className="text-lg font-semibold text-neutral-200">{counters.llm_usage?.tokens_out ?? 0}</p>
                        </div>
                    </div>
                )}

                <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
                    <Filter size={12} className="text-neutral-500" />
                    <select
                        value={creatorId}
                        onChange={(e) => setCreatorId(e.target.value)}
                        data-testid="autonomous-rooms-filter-creator"
                        className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1 text-neutral-200"
                    >
                        <option value="">All creators</option>
                        {creators.map((c) => (
                            <option key={c.virtual_creator_id} value={c.virtual_creator_id}>{c.name}</option>
                        ))}
                    </select>
                    <select
                        value={roomType}
                        onChange={(e) => setRoomType(e.target.value)}
                        data-testid="autonomous-rooms-filter-type"
                        className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1 text-neutral-200"
                    >
                        {ROOM_TYPES.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                {loading && items.length === 0 ? (
                    <div className="text-sm text-neutral-500">Loading rooms…</div>
                ) : items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#1A1A1A] p-8 text-center text-sm text-neutral-500" data-testid="autonomous-rooms-empty">
                        <div className="text-3xl mb-2">🤖</div>
                        No AI rooms yet. The cron publishes daily (max 3). Admins can trigger it manually.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="autonomous-rooms-list">
                        {items.map((r) => (
                            <div
                                key={r.id}
                                data-testid={`autonomous-room-card-${r.id}`}
                                className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4 hover:border-[#CCCCCC]/40 transition-all"
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1">
                                        <h3 className="text-sm font-semibold text-neutral-100 leading-snug">{r.title}</h3>
                                        <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-1">
                                            {r.room_type === "video" ? <Video size={10} /> : <Mic size={10} />}
                                            <span className="capitalize">{r.room_type}</span>
                                            <span>·</span>
                                            <span>{fmt(r.scheduled_start)}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#FFFFFF]/20 to-[#CCCCCC]/20 border border-[#CCCCCC]/30 text-[#CCCCCC] inline-flex items-center gap-1">
                                            <Bot size={10} /> AI
                                        </span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border capitalize ${STATUS_STYLE[r.status] || STATUS_STYLE.scheduled}`}>
                                            {r.status}
                                        </span>
                                    </div>
                                </div>
                                <p className="text-xs text-neutral-400 line-clamp-3 mb-3">{r.description}</p>
                                <div className="flex items-center justify-between text-[11px]">
                                    <Link
                                        to={`/ai/creator/${r.virtual_creator_id}`}
                                        className="text-[#CCCCCC] hover:underline truncate"
                                        data-testid={`autonomous-room-host-${r.id}`}
                                    >
                                        Hosted by @{r.creator?.name || r.host_username}
                                    </Link>
                                    <div className="flex items-center gap-2 text-neutral-500">
                                        <span>{r.duration_minutes || 45} min</span>
                                        <span>·</span>
                                        <span>{r.participant_count} joined</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {items.length < total && (
                    <div className="mt-6 text-center">
                        <button
                            onClick={() => load(false)}
                            disabled={loading}
                            data-testid="autonomous-rooms-load-more"
                            className="text-xs px-4 py-2 rounded-full bg-white text-black disabled:opacity-50"
                        >
                            {loading ? "Loading…" : `Load more (${total - items.length} remaining)`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
